import { useCallback } from "react";

import { datadogLogs } from "@datadog/browser-logs";

import { InventoryChangeReason } from "__graphql__/types";
import {
  CollaborativeInboundProgressedMethod,
  CollaborativeInboundUpdateState,
  CollaborativeListPreparationMethod,
  InboundVerificationReason,
  CollaborativeInboundProgressedAction,
  CollaborativeInboundStage,
  OutboundOrderCancellationReason,
} from "analytics/events";
import { ListVerificationCheckTypeKeys } from "flows/Inbound/models/listVerificationCheck/types";
import { InboundUnitAddOrigin } from "flows/Inbound/stores/inboundStore/types";
import { useInboundStore } from "flows/Inbound/stores/inboundStore/useInboundStore";
import { extractInboundPlan } from "flows/Inbound/stores/inboundStore/utils";
import { useAnalytics } from "shared/hooks/useAnalytics";
import { isNullOrUndefined } from "utils/tsHelpers";

const VERIFICATION_REASON_MAP: Record<ListVerificationCheckTypeKeys, InboundVerificationReason> = {
  [ListVerificationCheckTypeKeys.InboundQuantityOfOne]: "quantity_equal_to_one",
  [ListVerificationCheckTypeKeys.LargeInboundQuantity]: "quantity_more_than_fifty",
  [ListVerificationCheckTypeKeys.HandlingUnitQuantityMismatch]: "quantity_handling_unit_mismatch",
  [ListVerificationCheckTypeKeys.MoreThan50PercentAboveExpected]:
    "quantity_above_expected_by_50_percent",
};

export const INBOUND_UNIT_ADD_ORIGIN_MAP: Record<
  InboundUnitAddOrigin,
  CollaborativeInboundProgressedMethod
> = {
  [InboundUnitAddOrigin.Scan]: CollaborativeInboundProgressedMethod.Scanned,
  [InboundUnitAddOrigin.TextSearch]: CollaborativeInboundProgressedMethod.TextSearched,
  [InboundUnitAddOrigin.NoEANSearch]: CollaborativeInboundProgressedMethod.AdvancedSearched,
};

export function useInboundEventTracker() {
  const { sendSegmentTrackEvent } = useAnalytics();
  // Send inbound state update based on specific events
  const sendInboundStateUpdate = useCallback(
    ({
      state,
      droppingListId = null,
      sscc = null,
      inboundingType = null,
      isManualUnclaim = false,
      creatorId = null,
      orderId = null,
    }: {
      state: CollaborativeInboundUpdateState;
      droppingListId?: string | null;
      sscc?: string | null;
      inboundingType?: string | null;
      isManualUnclaim?: boolean;
      creatorId?: string | null;
      orderId?: string | null;
    }) => {
      if (isNullOrUndefined(droppingListId) || isNullOrUndefined(state)) {
        datadogLogs.logger.info(
          `Missing droppingListId or state in sendInboundStateUpdate -  droppingListId: ${droppingListId}, state: ${state}`,
        );
        return;
      }
      const listPreparationMethod =
        creatorId === "system"
          ? CollaborativeListPreparationMethod.Automatic
          : CollaborativeListPreparationMethod.Manual;
      sendSegmentTrackEvent("inboundStateUpdated", {
        state,
        dropping_list_id: droppingListId,
        sscc,
        inbounding_type: inboundingType,
        is_manual_unclaim: isManualUnclaim,
        list_preparation_method: listPreparationMethod,
        order_id: orderId,
      });
    },
    [sendSegmentTrackEvent],
  );

  // Track verification progress
  const sendInboundVerificationUpdate = useCallback(
    ({ checkKey, currentCheckIndex }: { checkKey: string; currentCheckIndex: number }) => {
      const { deliverySSCC, sharedListId, verificationChecks, inboundUnitsStockUpdates } =
        useInboundStore.getState();
      if (isNullOrUndefined(sharedListId)) {
        datadogLogs.logger.info("Missing droppingListId in sendInboundVerificationUpdate");
        return;
      }

      const check = verificationChecks.find((c) => c.key === checkKey);
      if (isNullOrUndefined(check)) {
        datadogLogs.logger.info(
          `Cannot find check with key ${checkKey} in sendInboundVerificationUpdate`,
        );
        return;
      }

      const inboundStockUpdate = extractInboundPlan(
        inboundUnitsStockUpdates[check.sku]?.stockUpdatePlan,
      );

      if (isNullOrUndefined(inboundStockUpdate)) {
        datadogLogs.logger.info(
          `Missing stock update for SKU ${check.sku} in sendInboundVerificationUpdate`,
        );
        return;
      }

      const quantityBeforeVerification =
        check.type === ListVerificationCheckTypeKeys.InboundQuantityOfOne ? 1 : check.data.quantity;

      sendSegmentTrackEvent("inboundVerificationProgressed", {
        dropping_list_id: sharedListId,
        sscc: deliverySSCC,
        product_sku: check.sku,
        verification_reason: VERIFICATION_REASON_MAP[check.type],
        quantity_before_verification: quantityBeforeVerification,
        quantity_after_verification: Math.max(inboundStockUpdate.quantityDelta, 0),
        running_number: currentCheckIndex + 1,
      });
    },
    [sendSegmentTrackEvent],
  );

  // Track outbound progress
  const sendOutboundUpdate = useCallback(
    ({
      listId,
      deliverySSCC,
      sku,
      updatedStockUpdatePlan,
      shelf,
    }: {
      listId: string;
      deliverySSCC: string | null;
      sku: string;
      updatedStockUpdatePlan: {
        quantityDelta: number;
        reason: InventoryChangeReason;
      }[];
      shelf: string | null;
    }) => {
      const { quantityExpired, quantityDamaged, quantityInbounded } = updatedStockUpdatePlan.reduce(
        (acc, { reason, quantityDelta }) => {
          switch (reason) {
            case InventoryChangeReason.outbound_delivery_product_expired:
              acc.quantityExpired += quantityDelta;
              break;
            case InventoryChangeReason.outbound_delivery_product_damaged:
              acc.quantityDamaged += quantityDelta;
              break;
            case InventoryChangeReason.inbound_goods_received:
              acc.quantityInbounded += quantityDelta;
              break;
            default:
              break;
          }
          return acc;
        },
        { quantityExpired: 0, quantityDamaged: 0, quantityInbounded: 0 },
      );

      sendSegmentTrackEvent("outboundProgressed", {
        action: "product_outbounded",
        quantity_expired: quantityExpired,
        quantity_damaged: quantityDamaged,
        quantity_perished: 0,
        quantity_received: quantityInbounded,
        dropping_list_id: listId,
        product_sku: sku,
        sscc: deliverySSCC,
        shelf_number: shelf,
      });
    },
    [sendSegmentTrackEvent],
  );

  // Track outbound order cancellation progress
  const sendOutboundOrderCancellationUpdate = useCallback(
    ({
      droppingListId,
      droppingListName,
      orderId,
      sku,
      newInboundQuantityDelta,
      updatedStockUpdatePlan,
    }: {
      droppingListId: string;
      droppingListName: string | null;
      orderId: string | null;
      sku: string;
      newInboundQuantityDelta: number;
      updatedStockUpdatePlan: {
        quantityDelta: number;
        reason: InventoryChangeReason;
      }[];
    }) => {
      const baseSegmentPayload = {
        dropping_list_id: droppingListId,
        order_id: orderId,
        product_sku: sku,
        dropping_list_name: droppingListName,
        inbounding_stage: CollaborativeInboundStage.ListDropping,
      };

      updatedStockUpdatePlan.forEach(({ reason, quantityDelta }) => {
        const quantity = Math.abs(quantityDelta);
        if (
          reason === InventoryChangeReason.outbound_order_cancellation_not_returned &&
          quantity > 0
        ) {
          sendSegmentTrackEvent("inboundProgressed", {
            ...baseSegmentPayload,
            action: CollaborativeInboundProgressedAction.ProductRemovedFromList,
            quantity,
            reason: OutboundOrderCancellationReason.NotReturned,
          });
        }

        if (
          reason === InventoryChangeReason.outbound_order_cancellation_not_sellable &&
          quantity > 0
        ) {
          sendSegmentTrackEvent("inboundProgressed", {
            ...baseSegmentPayload,
            action: CollaborativeInboundProgressedAction.ProductRemovedFromList,
            quantity,
            reason: OutboundOrderCancellationReason.NotSellable,
          });
        }
      });
      sendSegmentTrackEvent("inboundProgressed", {
        ...baseSegmentPayload,
        action: CollaborativeInboundProgressedAction.ProductUpdatedQuantity,
        quantity: newInboundQuantityDelta,
        reason: null,
      });
    },
    [sendSegmentTrackEvent],
  );

  return {
    sendInboundStateUpdate,
    sendInboundVerificationUpdate,
    sendOutboundUpdate,
    sendOutboundOrderCancellationUpdate,
  };
}
